import { useHistory } from 'react-router-dom';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { PAGE_URLS } from '../../../../helpers/endpoints';
import React, { useEffect, useState, Suspense } from 'react';
import { Button, ConfigProvider, Dropdown, Row, Table, Tag } from 'antd';
import trTR from 'antd/es/locale/tr_TR';
import { MoreOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getCourierWithFilter } from '../../../../store/requests/courier';
import { useSelector } from 'react-redux';
import {
  COURIER_TYPE_COLORS,
  COURIERS_AWAITING_APPROVAL_STATUS,
  ORDER_TYPES,
  COURIERS_AWAITING_COLORS
} from '../../../../helpers/constants';
const Edit = React.lazy(() => import('./edit'));

const CouriersAwaitingApproval = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { t } = useTranslation();
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [25, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 25,
    },
  });
  const [data, setData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const openSortFeature = !!data.length;

  useEffect(() => {
    fetchData();

    return () => {
      setData([]);
    };
  }, []);

  const fetchData = async (pagination = tableParams.pagination) => {
    setLoading(true);
    try {
      const queryString = getQueryString(pagination);
      const basePayload = {
        userRole: 'COURIER',
        statuses: [
          COURIERS_AWAITING_APPROVAL_STATUS.WAITING_APPROVAL,
          COURIERS_AWAITING_APPROVAL_STATUS.WAITING_FINANCE_APPROVAL,
        ],
        isActive: true,
      };

      const response = await getCourierWithFilter(basePayload, queryString);
      const { content, page } = response;

      setData(content);
      setTableParams((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          total: page.totalElements,
          current: page.number + 1,
          pageSize: page.size,
        },
      }));
    } finally {
      setLoading(false);
    }
  };

  const getQueryString = (pagination) => {
    let queryString = '';
    queryString = `${queryString}&page=${pagination.current - 1}&size=${pagination.pageSize}`;

    if (pagination.sorter) {
      queryString = `${queryString}&sort=${pagination.sorter.field},${ORDER_TYPES[pagination.sorter.order]}`;
    }
    return queryString;
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const updatedParams = {
      ...tableParams,
      pagination,
      sorter,
    };
    setTableParams(updatedParams);
    fetchData(pagination);
  };

  const columns = [
    {
      title: t('fullName'),
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: openSortFeature,
      showSorterTooltip: false,
    },
    {
      title: t('firm'),
      dataIndex: 'firmName',
      key: 'firmName',
      sorter: openSortFeature,
      showSorterTooltip: false,
      render: (firm) => (firm ? firm : '-'),
    },
    {
      title: t('courierCode'),
      dataIndex: 'employeeNumber',
      key: 'employeeNumber',
      sorter: openSortFeature,
      showSorterTooltip: false,
      render: (employeeNumber) => (employeeNumber ?? '-'),
    },
    {
      title: t('phoneNumber'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      sorter: openSortFeature,
      showSorterTooltip: false,
    },
    {
      title: t('courierType'),
      dataIndex: 'courierType',
      width: 110,
      sorter: openSortFeature,
      showSorterTooltip: false,
      render: (courierType) => (
        <Tag color={COURIER_TYPE_COLORS[courierType]}>
          {t(`courierType_${courierType}`)}
        </Tag>
      ),
    },
    {
      title: t('currentNumber'),
      dataIndex: 'currentNumber',
      sorter: openSortFeature,
      showSorterTooltip: false,
    },
     {
      title: t('createdByCourier'),
      dataIndex: 'createdUser',
      key: 'createdUser',
      width: 230,
      sorter: openSortFeature,
      showSorterTooltip: false,
      render: (createdUser) => (createdUser ?? '-'),
    },
    {
      title: t('createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 240,
      sorter: openSortFeature,
      showSorterTooltip: false,
      render: (createdAt) => createdAt ? new Date(createdAt).toLocaleDateString('tr-TR') : '-',
    },
    {
      title: t('vehicleStatus'),
      dataIndex: 'isActive',
      width: 100,
      sorter: openSortFeature,
      showSorterTooltip: false,
      render: (isActive) =>
        isActive ? (
          <Tag color={'green'}>{t('active')}</Tag>
        ) : (
          <Tag color={'red'}>{t('passive')}</Tag>
        ),
    },
    {
      title: t('zoneManagement.approvalStatus'),
      dataIndex: 'status',
      key: 'status',
      sorter: openSortFeature,
      showSorterTooltip: false,
      render: (status) => {
        const colorIndex = Object.values(COURIERS_AWAITING_APPROVAL_STATUS).indexOf(status);
        const color = colorIndex >= 0 ? COURIERS_AWAITING_COLORS[colorIndex + 1]?.color : 'gray';

        return (
          <Tag color={color}>
            {t(`${status}`)}
          </Tag>
        );
      },
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [          
                {
                  label: t('showDetails'),
                  icon: <EyeOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                    setSelectedItem(dataItem);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItem(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  return (
    <div>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={data}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isEditModalOpen && (
          <Edit
            selectedCouiers={selectedItem}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={() => fetchData()}
          />
        )}
      </Suspense>
    </div>
  );
};

export default CouriersAwaitingApproval;
